import { useMemo, useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { rolesService, Role } from '@/services/roles.service';

interface UsePermissionsReturn {
  hasPermission: (resource: string, action: string) => boolean;
  loading: boolean;
  userRole: Role | null;
}

/**
 * Hook pour vérifier les permissions de l'utilisateur connecté
 * @returns {UsePermissionsReturn} Objet contenant hasPermission, loading et userRole
 */
export const usePermissions = (): UsePermissionsReturn => {
  const { user } = useAuth();
  const [userRole, setUserRole] = useState<Role | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRole = async () => {
      if (user?.role_id) {
        try {
          const role = await rolesService.getById(user.role_id);
          setUserRole(role);
        } catch (error) {
          console.error('Erreur lors du chargement du rôle:', error);
          setUserRole(null);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
        setUserRole(null);
      }
    };
    loadRole();
  }, [user?.role_id]);

  const hasPermission = useMemo(() => {
    return (resource: string, action: string): boolean => {
      if (!userRole || !userRole.permissions) {
        return false;
      }

      // Cas spécial: permissions = ["*"] ou ["all"] signifie toutes les permissions
      if (Array.isArray(userRole.permissions)) {
        // Vérifier si c'est un tableau de strings (ancien format)
        if (userRole.permissions.length > 0 && typeof userRole.permissions[0] === 'string') {
          const permissionsArray = userRole.permissions as unknown as string[];
          if (permissionsArray.includes('*') || permissionsArray.includes('all')) {
            console.log('✅ Permission accordée (admin avec toutes les permissions)');
            return true;
          }
        }
        
        // Format moderne: tableau d'objets { resource, actions }
        const permission = userRole.permissions.find(
          (p: any) => p && typeof p === 'object' && p.resource === resource
        );
        
        if (permission && Array.isArray(permission.actions)) {
          return permission.actions.includes(action);
        }
      }
      
      return false;
    };
  }, [userRole]);

  return { hasPermission, loading, userRole };
};
